/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { Fragment, useEffect, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  Text,
  Box,
  IconButton,
  Button,
  ModalFooter,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Flex,
  Tooltip,
  useToast,
  Skeleton,
} from '@chakra-ui/core';
import { CloseIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { RiErrorWarningLine } from 'react-icons/ri';
import isNil from 'lodash/isNil';
import colors from '../../styles/colors';
import useCandidate from '../../app/components/useCandidate';
import { CandidateMessagingView } from '../messaging/CandidateMessagingView';
import { useStoreActions, useStoreState } from '../../models/hooks';
import BackgroundCheckIcon from '../../assets/img/certn-logo.svg';
import { CandidateStatus } from '../../firebase/firestore/documents/candidate';
import { DismissConfirmationModal } from '../messaging/seeker/header/actionsDropdown/DismissConfirmationModal';
import { HireConfirmationModal } from '../messaging/seeker/header/actionsDropdown/HireConfirmationModal';
import { HireWithRequisitionModal } from '../messaging/seeker/header/actionsDropdown/HireWithRequisitionModal';
import { HireRejectConfirmationModal } from '../messaging/seeker/header/actionsDropdown/HireRejectConfirmationModal';
import { HireRejectCandidateModal } from '../messaging/seeker/header/actionsDropdown/HireRejectCandidateModal';
import { usePositionBackgroundCheckSnapshot } from '../messaging/seeker/header/actionsDropdown/usePositionBackgroundCheckSnapshot';
import usePosition from '../messaging/sidebar/seekerList/usePosition';
import { useCurrentUserProfile } from '../../app/hooks/useCurrentUserProfile';
import {
  isCandidateAlreadyHired,
  isCandidateAlreadyDismissed,
  isCandidateReadOnly,
  isCandidateFromFollowedOrWatchedPosition,
  isCandidateHireInReview,
} from '../../services/CandidateService';
import { useUserPermission } from '../../services/permission/useUserPermission';
import { EmployeePermissionsEnum } from '../administration/team/teamPermission/teamPermission.constant';
import PermissionWrapper from '../../services/permission/PermissionWrapper';
import { OfferMadePositionsView } from '../messaging/seeker/header/actionsDropdown/OfferMadePositionsView';
import { RemoteConfigKey } from '../../firebase/remoteConfig';
import { PERMISSION_MODE } from '../../services/permission/permission.interface';
import HttpClientWithRetry from '../../http/client/HttpClientWithRetry';
import { CandidateController } from '../../controllers/candidateController';
import { useSeekerDocumentSnapshot } from '../../firebase/firestore/hooks';
import { MessageAfterHiringModal } from '../messaging/seeker/header/actionsDropdown/MessageAfterHiringModal';

export type BackgroundCheckCompletedReviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  currentCandidateId: string;
  currentDocId?: string;
  currentTask?: string;
  selectedToDoStatus?: string;
};

export const BackgroundCheckCompletedReviewModal = ({
  isOpen,
  onClose,
  currentCandidateId,
  currentDocId,
  currentTask,
  selectedToDoStatus,
}: BackgroundCheckCompletedReviewModalProps): JSX.Element => {
  const { t } = useTranslation();
  const cancelRef = React.useRef(null);
  const currentCandidate = useCandidate(currentCandidateId);
  const candidate = currentCandidate?.candidate;
  const userId = useStoreState((states) => states.app.user?.id);
  const { updateRecruiterActionStatus } = useStoreActions((actions) => actions.toDoList);
  const { currentUserProfile } = useCurrentUserProfile();
  const { checkPermission } = useUserPermission();

  const position = usePosition(candidate?.newHireData?.positionId || candidate?.position);
  const positionScreeningOption = usePositionBackgroundCheckSnapshot(candidate?.position);

  /* istanbul ignore next */
  const requisitionEnabled = useStoreState((state) => state.app.accounts?.configuration?.requisitionEnabled);
  const isAccountAutomaticHireDisabled = useStoreState((s) => s.app.accounts?.configuration?.automaticHireDisabled);
  const isPositionAutomaticHireDisabled = position?.automaticHireDisabled;
  const isAutomaticHireDisabledBothInAccountAndPosition = isAccountAutomaticHireDisabled && isPositionAutomaticHireDisabled;
  const accountStore = useStoreState((s) => s.app.accounts);

  const isShowWarningOnHiringCandidateFeatureEnabled: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.SHOW_WARNING_ON_HIRING_CANDIDATE_FEATURE];
  });

  const isUserDeactivated = Boolean(candidate && (candidate.deactivated || candidate.scheduledDeletion || candidate.dismissed));

  const warningMessage = isShowWarningOnHiringCandidateFeatureEnabled
    ? currentUserProfile?.config?.showWarningOnCandidateHire ?? true
    : false;

  const [isCertnEnabled, setCertnEnabled] = useState<boolean>(false);
  const [certnReportLoading, setCertnReportLoading] = useState<boolean>(false);
  const { client } = useStoreState((s) => s.http);

  const selectedSeeker = useSeekerDocumentSnapshot(candidate?.seeker);

  const isCertnFeatureEnabled = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_CERTN_FEATURE];
  });

  const toast = useToast();

  useEffect(() => {
    if (!isNil(accountStore) && accountStore.configuration && accountStore.configuration.certnEnabled) {
      setCertnEnabled(accountStore.configuration.certnEnabled);
    } else {
      setCertnEnabled(false);
    }
  }, [accountStore]);

  const isCurrentCandiateNeitherFollowedNorWatched = candidate
    ? !isCandidateFromFollowedOrWatchedPosition(candidate, currentUserProfile?.id)
    : false;

  const hireActionDisabled = candidate
    ? isCandidateAlreadyHired(candidate) ||
      isCandidateAlreadyDismissed(candidate) ||
      isCandidateReadOnly(candidate) ||
      isCandidateHireInReview(candidate.computedStatus?.status)
    : true;

  // Local disclosure states for sub-modals
  const { isOpen: isDismissModalOpen, onOpen: onDismissModalOpen, onClose: onDismissModalClose } = useDisclosure();
  const { isOpen: isHireModalOpen, onOpen: onHireModalOpen, onClose: onHireModalClose } = useDisclosure();
  const { isOpen: isHireRejectModalOpen, onOpen: onHireRejectModalOpen, onClose: onHireRejectModalClose } = useDisclosure();
  const { isOpen: isOpenHireCandidate, onOpen: onOpenHireCandidate, onClose: onCloseHireCandidate } = useDisclosure();
  const { isOpen: isOpenWarning, onOpen: onOpenWarning, onClose: onCloseWarning } = useDisclosure();
  const {
    isOpen: isMessageAferHiringModalOpen,
    onOpen: onMessageAferHiringModalOpen,
    onClose: onMessageAferHiringModalClose,
  } = useDisclosure();

  const updateToCompleted = async () => {
    if (currentDocId && userId && currentTask) {
      await updateRecruiterActionStatus({
        userId,
        docId: currentDocId,
        status: 'COMPLETED',
        category: currentTask,
        subCategory: null,
        candidateId: currentCandidateId,
      });
    }
  };

  const handleActionSuccess = async () => {
    await updateToCompleted();
    onClose();
  };

  const getBackgroundCheckStatusText = () => {
    // istanbul ignore next
    if (selectedToDoStatus === 'bgCheckCleared') {
      return t('certn:backgroundCheckCompletedClear');
    }
    return t('certn:backgroundCheckCompletedNeedsReview');
  };

  // istanbul ignore next
  const showPdf = () => {
    const controller = new CandidateController(client as HttpClientWithRetry);

    const getPdf = async () => {
      setCertnReportLoading(true);

      if (candidate) {
        try {
          await controller.getCertnPdf(candidate.id).then((response: string) => {
            const file = new Blob([response], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
          });
        } catch (error) {
          toast({
            title: t('certn:screeningError'),
            description: t('certn:incompleteScreeningError'),
            duration: 3000,
            isClosable: true,
            status: 'warning',
          });
        }
      }
      setCertnReportLoading(false);
    };
    // eslint-disable-next-line
    getPdf();
  };

  // istanbul ignore next
  const hireActionHandler = (status: CandidateStatus | undefined) => {
    let hireButton;

    if (status === CandidateStatus.OfferMade) {
      hireButton = (
        <PermissionWrapper
          permissions={[EmployeePermissionsEnum.HR_REQUEST]}
          mode="disable"
          enableTooltip
          disableDisplayType="block"
        >
          <Button
            data-testid="OfferMadeAction"
            colorScheme="blue"
            onClick={onHireRejectModalOpen}
            isDisabled={
              (Boolean(position?.hireFormTemplateId) &&
                !checkPermission({ permissions: [EmployeePermissionsEnum.HR_REQUEST] })) ||
              isUserDeactivated ||
              isCurrentCandiateNeitherFollowedNorWatched
            }
          >
            {t('messaging:actions.hire.hire')}
          </Button>
        </PermissionWrapper>
      );
    } else if (status === CandidateStatus.OfferRejected) {
      hireButton = (
        <Button
          data-testid="OfferRejectedAction"
          colorScheme="blue"
          onClick={onHireRejectModalOpen}
          isDisabled={isUserDeactivated}
        >
          {t('messaging:actions.hire.hire')}
        </Button>
      );
    } else if (candidate?.hireDetailsStatus === 'approved') {
      hireButton = (
        // istanbul ignore next
        <Fragment>
          {isAutomaticHireDisabledBothInAccountAndPosition && (
            /* istanbul ignore next */
            <Button
              data-testid="HireRejectAction"
              colorScheme="blue"
              onClick={onOpenHireCandidate}
              isDisabled={isUserDeactivated || isCurrentCandiateNeitherFollowedNorWatched}
            >
              {t('messaging:actions.hire.hire')}
            </Button>
          )}
        </Fragment>
      );
    } else {
      hireButton = (
        <Button
          data-testid="OfferMadeDisabledAction"
          colorScheme="blue"
          onClick={() => {
            if (candidate && selectedSeeker?.offeredOrHiredAccounts?.includes(candidate.account)) {
              onOpenWarning();
            } else {
              onHireModalOpen();
            }
          }}
          isDisabled={
            hireActionDisabled || candidate?.offerAcceptanceStatus?.offerAccepted || isUserDeactivated || isNil(selectedSeeker)
          }
        >
          {t('messaging:actions.hire.offerMade')}
        </Button>
      );
    }

    return hireButton;
  };

  return (
    <Fragment>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl" scrollBehavior="inside">
        <ModalOverlay />

        <ModalContent position="absolute" transform="none" overflow="auto" height="75vh">
          <ModalCloseButton
            as={IconButton as any}
            icon={<CloseIcon boxSize={3} />}
            aria-label="Close modal"
            style={{
              cursor: 'pointer',
              backgroundColor: colors.white,
              borderRadius: '50%',
              border: `1px solid ${colors.gray[300]}`,
              padding: 20,
            }}
            _hover={{ background: `${colors.gray[100]} !important` }}
          />

          <ModalBody
            borderTop={`1px solid ${colors.gray[100]}`}
            overflow="auto"
            display="flex"
            flexDirection="row"
            flex="1"
            height="100%"
          >
            <Box mr={5} flex={1} minWidth="50%" height="100%" overflowY="auto">
              <CandidateMessagingView candidateId={currentCandidateId} hideHeader isMessagingOnly />
            </Box>
            <Box display="flex" flexDirection="column" flex={1} minHeight="50%" overflowY="auto" height="100%" paddingBottom={4}>
              <Box>
                <ModalHeader paddingLeft={0} pb={0} minHeight="30px">
                  {currentCandidate.loading ? (
                    <Skeleton height="27px" width="250px" />
                  ) : (
                    <Text fontSize={18} color={colors.neutralGray[700]} fontWeight={400} display="flex">
                      <Text fontWeight="bold" mr={1}>
                        {t('certn:candidateLabel')}
                      </Text>

                      <Text>{`${currentCandidate?.candidate?.firstName} ${currentCandidate?.candidate?.lastName}`}</Text>
                    </Text>
                  )}
                </ModalHeader>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius={16}
                  mt={6}
                  backgroundColor={colors.gray[250]}
                  py={8}
                  px={6}
                >
                  <img src={BackgroundCheckIcon} alt="background check" height={124} width={124} />
                  <Text data-testid="backgroundCheckStatusText" color={colors.green[400]} fontWeight={700} fontSize={20} pt={2}>
                    {getBackgroundCheckStatusText()}
                  </Text>
                  {isCertnFeatureEnabled && isCertnEnabled && candidate?.screeningDetails && (
                    <Tooltip label={t('certn:bgCheckStatusLabel')} hasArrow fontSize="sm">
                      <PermissionWrapper
                        permissions={[EmployeePermissionsEnum.BACKGROUND_CHECK_REVIEW]}
                        mode={PERMISSION_MODE.disable}
                        enableTooltip
                      >
                        <Button
                          mt={4}
                          backgroundColor={colors.white}
                          fontWeight={700}
                          fontSize={16}
                          data-testid="loadHireDetailButton"
                          cursor="pointer"
                          onClick={showPdf}
                          isLoading={certnReportLoading}
                          color={colors.neutralGray[700]}
                        >
                          {t('certn:viewReportBtn')}
                        </Button>
                      </PermissionWrapper>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              <ModalFooter pr="3px" marginTop="auto" justifyContent="flex-end">
                <Button data-testid="dismissButton" colorScheme="red" mr={3} onClick={onDismissModalOpen}>
                  {t('messaging:actions.dismiss.label')}
                </Button>
                {currentCandidate.loading ? (
                  <Skeleton height="40px" width="140px" borderRadius="md" />
                ) : (
                  hireActionHandler(candidate?.computedStatus?.status)
                )}
              </ModalFooter>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Sub-modals */}
      {isDismissModalOpen && (
        <DismissConfirmationModal
          isOpen={isDismissModalOpen}
          disabled={false}
          onClose={onDismissModalClose}
          isMessagingView={false}
          candidateFromProps={candidate}
          onSuccessCallback={handleActionSuccess}
        />
      )}

      {isHireModalOpen && (
        <Box>
          {requisitionEnabled ? (
            /* istanbul ignore next */
            <HireWithRequisitionModal
              isOpen={isHireModalOpen}
              disabled={false}
              onClose={onHireModalClose}
              requisitionEnabled={requisitionEnabled}
              candidateFromProps={candidate}
              onSuccessCallback={handleActionSuccess}
            />
          ) : (
            <HireConfirmationModal
              positionSalaryDetails={positionScreeningOption?.proposedSalary}
              isOpen={isHireModalOpen}
              disabled={false}
              onClose={onHireModalClose}
              requisitionEnabled={requisitionEnabled}
              candidateFromProps={candidate}
              onSuccessCallback={handleActionSuccess}
            />
          )}
        </Box>
      )}

      {isHireRejectModalOpen && (
        <HireRejectConfirmationModal
          isOpen={isHireRejectModalOpen}
          warningMessage={warningMessage}
          messageModalOpen={onMessageAferHiringModalOpen}
          disabled={false}
          onClose={onHireRejectModalClose}
          candidateFromProps={candidate}
          onSuccessCallback={handleActionSuccess}
        />
      )}

      {isMessageAferHiringModalOpen && (
        <MessageAfterHiringModal isOpen={isMessageAferHiringModalOpen} onClose={onMessageAferHiringModalClose} />
      )}

      {isOpenHireCandidate && (
        /* istanbul ignore next */
        <HireRejectCandidateModal
          isOpen={isOpenHireCandidate}
          onClose={onCloseHireCandidate}
          disabled={false}
          candidateIdFromProps={currentCandidateId}
          onSuccessCallback={handleActionSuccess}
        />
      )}

      {isOpenWarning && (
        <AlertDialog
          isOpen={isOpenWarning}
          leastDestructiveRef={cancelRef}
          onClose={onCloseWarning}
          isCentered
          size="5xl"
          scrollBehavior="inside"
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontWeight="bold" fontSize="22px" pb="0px">
                <Box>{t('messaging:offerMadewarning.title')}</Box>

                <Flex background="#FFFFF0" color="#B7791F" borderLeft="6px solid #FFB323" p="10px" alignItems="center" mt="10px">
                  <RiErrorWarningLine size="30px" color="#FFB323" />
                  <Text ml="10px" fontSize="16px">
                    {t('messaging:offerMadewarning.subtitle')}
                  </Text>
                </Flex>
                <Text fontWeight="900" fontSize="20px" color={colors.blue.default} mt="12px">
                  {t('messaging:offerMadewarning.offerHistory')}
                </Text>
              </AlertDialogHeader>

              <AlertDialogBody>
                <OfferMadePositionsView seekerId={candidate?.seeker} />
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button onClick={onCloseWarning} colorScheme="red">
                  {t('messaging:actions.meet.confirmationModal.cancelButton')}
                </Button>
                <Button
                  ref={cancelRef}
                  data-testid="onAcceptButton"
                  colorScheme="blue"
                  onClick={
                    /* istanbul ignore next */ () => {
                      onCloseWarning();
                      onHireModalOpen();
                    }
                  }
                  ml={3}
                >
                  {t('messaging:offerMadewarning.continueAnyWay')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </Fragment>
  );
};
